/**
 * Formatting helpers for classic D&D 5e MM-style stat blocks from SRD JSON (Open5e / 5e API shape).
 */

const ABILITY_SHORT = {
  STR: 'Str',
  DEX: 'Dex',
  CON: 'Con',
  INT: 'Int',
  WIS: 'Wis',
  CHA: 'Cha',
};

export function abilityMod(score) {
  if (score == null || Number.isNaN(Number(score))) return null;
  const s = Number(score);
  return Math.floor((s - 10) / 2);
}

export function formatAbilityMod(score) {
  const m = abilityMod(score);
  if (m == null) return '—';
  return m >= 0 ? `+${m}` : `${m}`;
}

export function capitalizeWords(str) {
  if (!str) return '';
  return str
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function formatBonus(value) {
  if (value == null) return '+0';
  const n = Number(value);
  return n >= 0 ? `+${n}` : `${n}`;
}

function parseHitDiceCount(hitDice) {
  if (!hitDice || typeof hitDice !== 'string') return 0;
  const m = hitDice.match(/^(\d+)d/i);
  return m ? parseInt(m[1], 10) : 0;
}

const ARMOR_TYPE_LABEL = {
  natural: 'Natural Armor',
  dex: null,
  armor: 'Armor',
  shield: 'Shield',
};

function armorTypeLabel(type) {
  if (!type) return null;
  const key = String(type).toLowerCase();
  if (key in ARMOR_TYPE_LABEL) return ARMOR_TYPE_LABEL[key];
  return capitalizeWords(String(type).replace(/_/g, ' '));
}

export function formatArmorClass(armorClass) {
  if (!armorClass || !Array.isArray(armorClass) || armorClass.length === 0) {
    return null;
  }
  return armorClass
    .map((entry) => {
      const label = armorTypeLabel(entry.type);
      if (label) {
        return `${entry.value} (${label})`;
      }
      return String(entry.value);
    })
    .join(', ');
}

export function formatHitPoints(monster) {
  if (monster.hit_points == null) return null;
  const n = parseHitDiceCount(monster.hit_dice);
  const conMod = abilityMod(monster.constitution) ?? 0;
  const bonus = n * conMod;
  const dice = monster.hit_dice || '';
  if (!dice) {
    return String(monster.hit_points);
  }
  const sign = bonus >= 0 ? '+' : '';
  return `${monster.hit_points} (${dice}${sign}${bonus})`;
}

export function formatSpeed(speed) {
  if (!speed || typeof speed !== 'object') return null;
  const parts = [];
  if (speed.walk) {
    parts.push(speed.walk);
  }
  const rest = Object.keys(speed)
    .filter((k) => k !== 'walk')
    .sort();
  rest.forEach((k) => {
    const v = speed[k];
    if (v) {
      parts.push(`${capitalizeWords(k)} ${v}`);
    }
  });
  return parts.length ? parts.join(', ') : null;
}

export function formatAbilityScores(monster) {
  const rows = [
    ['STR', monster.strength],
    ['DEX', monster.dexterity],
    ['CON', monster.constitution],
    ['INT', monster.intelligence],
    ['WIS', monster.wisdom],
    ['CHA', monster.charisma],
  ];
  return rows.map(([abbr, score]) => ({
    abbr,
    score,
    mod: formatAbilityMod(score),
  }));
}

function collectSavingThrows(proficiencies) {
  const saves = [];
  for (const p of proficiencies || []) {
    const name = p.proficiency?.name || '';
    const m = name.match(/^Saving Throw:\s*(\w+)/i);
    if (m) {
      const ab = m[1].toUpperCase();
      const label = ABILITY_SHORT[ab] || capitalizeWords(m[1]);
      saves.push(`${label} ${formatBonus(p.value)}`);
    }
  }
  return saves;
}

export function formatSavingThrows(proficiencies) {
  const saves = collectSavingThrows(proficiencies);
  if (!saves.length) return null;
  return `Saving Throws ${saves.join(', ')}`;
}

export function getSavingThrowsEntry(proficiencies) {
  const saves = collectSavingThrows(proficiencies);
  if (!saves.length) return null;
  return { label: 'Saving Throws', value: saves.join(', ') };
}

function collectSkills(proficiencies) {
  const skills = [];
  for (const p of proficiencies || []) {
    const name = p.proficiency?.name || '';
    const m = name.match(/^Skill:\s*(.+)/i);
    if (m) {
      skills.push(`${m[1].trim()} ${formatBonus(p.value)}`);
    }
  }
  return skills;
}

export function formatSkills(proficiencies) {
  const skills = collectSkills(proficiencies);
  if (!skills.length) return null;
  return `Skills ${skills.join(', ')}`;
}

export function getSkillsEntry(proficiencies) {
  const skills = collectSkills(proficiencies);
  if (!skills.length) return null;
  return { label: 'Skills', value: skills.join(', ') };
}

const SENSE_ORDER = ['blindsight', 'darkvision', 'tremorsense', 'truesight'];

export function formatSenses(senses) {
  if (!senses || typeof senses !== 'object') return null;
  const parts = [];
  SENSE_ORDER.forEach((key) => {
    const v = senses[key];
    if (v) {
      parts.push(`${capitalizeWords(key)} ${v}`);
    }
  });
  Object.keys(senses).forEach((key) => {
    if (key === 'passive_perception' || SENSE_ORDER.includes(key)) return;
    const v = senses[key];
    if (v && typeof v === 'string') {
      parts.push(`${capitalizeWords(key)} ${v}`);
    }
  });
  if (senses.passive_perception != null) {
    parts.push(`passive Perception ${senses.passive_perception}`);
  }
  return parts.length ? parts.join(', ') : null;
}

export function getChallengeEntry(monster) {
  const cr = monster.challenge_rating;
  const xp = monster.xp;
  if (cr == null && xp == null) return null;
  let value;
  if (xp != null && cr != null) {
    value = `${cr} (${xp.toLocaleString()} XP)`;
  } else if (cr != null) {
    value = String(cr);
  } else {
    value = `${xp.toLocaleString()} XP`;
  }
  return { label: 'Challenge', value };
}

export function formatChallenge(monster) {
  const e = getChallengeEntry(monster);
  return e ? `Challenge ${e.value}` : null;
}

export function getLanguagesEntry(monster) {
  if (monster.languages === undefined) return null;
  const value = monster.languages === '' ? '—' : monster.languages;
  return { label: 'Languages', value };
}

export function getSensesEntry(senses) {
  const value = formatSenses(senses);
  if (!value) return null;
  return { label: 'Senses', value };
}

export function formatCreatureTypeLine(monster) {
  const size = monster.size || '';
  let type = monster.type || 'creature';
  if (monster.subtype) {
    type += ` (${monster.subtype})`;
  }
  const align = monster.alignment ? capitalizeWords(monster.alignment) : '';
  if (align) {
    return `${size} ${type}, ${align}`.trim();
  }
  return `${size} ${type}`.trim();
}

/** SRD lists are usually strings; condition_immunities uses { name, index, url } objects. */
function formatStatBlockListItem(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'object' && item.name) return item.name;
  return String(item);
}

export function getCommaListEntry(label, items) {
  if (!items || !items.length) return null;
  const value = items.map(formatStatBlockListItem).filter(Boolean).join(', ');
  if (!value) return null;
  return { label, value };
}

export function formatCommaList(header, items) {
  const e = getCommaListEntry(header, items);
  return e ? `${e.label} ${e.value}` : null;
}

export function formatNamedBlocks(entries, emptyNote) {
  if (!entries || !entries.length) {
    return emptyNote ? [{ name: null, desc: emptyNote }] : [];
  }
  return entries
    .filter((e) => e && (e.name || e.desc))
    .map((e) => ({
      name: e.name || null,
      desc: (e.desc || '').trim(),
    }));
}
